import { useEffect, useReducer, useRef, useState } from 'react';
import { either } from 'fp-ts';
import { StatusCode } from 'api/grpc/resources';
import { getModelVersionTE } from 'api/models/getModelVersionTE';

const MODEL_LOADING_STATUS_CODES = [
  StatusCode.MODEL_TRAINING,
  StatusCode.MODEL_QUEUED_FOR_TRAINING,
  StatusCode.MODEL_UPLOADING,
  StatusCode.MODEL_MODIFY_PENDING,
  StatusCode.MODEL_EVALUATING,
  StatusCode.MODEL_DEPLOYING,
  StatusCode.MODEL_LOADING,
  StatusCode.MODEL_BUSY_PLEASE_RETRY,
];

export type ModelVersionStatus = 'idle' | 'loading' | 'error' | 'success' | 'processing';

export type ModelVersionStatusData = {
  description: string;
  loading: boolean;
  status?: ModelVersionStatus;
  title: string;
};

type ModelVersionStatusAction = { type: ModelVersionStatus; data?: ModelVersionStatusData };

const ModelVersionStatusReducer = (state: ModelVersionStatusData, action: ModelVersionStatusAction): ModelVersionStatusData => {
  switch (action.type) {
    case 'loading':
      return {
        ...state,
        ...action.data,
        loading: true,
        status: action.type,
      };
    case 'processing':
      return {
        ...state,
        ...action.data,
        loading: true,
        status: action.type,
      };
    case 'success':
      return {
        ...state,
        ...action.data,
        loading: false,
        status: action.type,
      };
    case 'error':
      return {
        ...state,
        ...action.data,
        loading: false,
        status: action.type,
      };
    default:
      return { ...state, status: 'idle' };
  }
};

const INITIAL_STATE: ModelVersionStatusData = {
  description: '',
  loading: false,
  status: 'idle',
  title: '',
};

const RETRY_LIMIT = 10;
const INTERVAL = 3000;

type useModelVersionStatusProps = {
  appId: string;
  modelId: string;
  modelVersionId: string;
  userOrOrgId: string;
};

export const useModelVersionStatus = ({ userOrOrgId, appId, modelId, modelVersionId }: useModelVersionStatusProps) => {
  const [retryLimit, setRetryLimit] = useState(RETRY_LIMIT);
  const [modelStatus, setModelStatus] = useReducer(ModelVersionStatusReducer, INITIAL_STATE);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const fetchModelStatus = async (retry: boolean) => {
    setModelStatus({
      type: 'loading',
      data: { ...INITIAL_STATE },
    });

    const response: CF.API.Models.GetModelVersion.Response | undefined = await fetchModelVersionStatus({
      userOrOrgId,
      appId,
      modelId,
      modelVersionId,
    });

    if (!response) throw new Error('Bad response!');

    const {
      model_version: { status },
    } = response;

    if (status && status.code === StatusCode.MODEL_TRAINED) {
      return setModelStatus({
        type: 'success',
        data: {
          description: '',
          loading: false,
          title: '',
        },
      });
    }

    const data = {
      description: status?.details?.split(/(?=[.-{])/).shift() || '',
      loading: MODEL_LOADING_STATUS_CODES.includes(status.code),
      title: status?.description || '',
    };

    if (!MODEL_LOADING_STATUS_CODES.includes(status.code)) {
      return setModelStatus({
        type: 'error',
        data,
      });
    }

    if (retry) {
      timeout.current = setTimeout(() => {
        setRetryLimit(retryLimit - 1);
      }, INTERVAL);
    }

    return setModelStatus({
      type: 'processing',
      data,
    });
  };

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);
    if (modelVersionId) fetchModelStatus(retryLimit > 0);
  }, [modelVersionId, retryLimit]);

  return modelStatus;
};

type fetchModelVersionStatusProps = {
  appId: string;
  modelId: string;
  modelVersionId: string;
  userOrOrgId: string;
};

const fetchModelVersionStatus = async ({ userOrOrgId, appId, modelId, modelVersionId }: fetchModelVersionStatusProps) => {
  const response = await getModelVersionTE({
    userOrOrgId,
    appId,
    modelId,
    modelVersionId,
  })();

  if (either.isRight(response)) {
    return response.right;
  } else {
    return undefined;
  }
};
